import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import "./App.css";
import "./styles/SweetAlert.css";

// Auth
import Login from "./pages/Auth/Login";
import ResetPassword from "./pages/Auth/ResetPassword";

// Admin Pages
import AdminLayout from "./components/Layout/AdminLayout";
import Dashboard from "./pages/Dashboard/Dashboard";
import TestSeriesList from "./pages/TestSeries/TestSeriesList";
import CreateTestSeries from "./pages/TestSeries/CreateTestSeries";
import EditTestSeries from "./pages/TestSeries/EditTestSeries";
import SectionsManager from "./pages/TestSeries/SectionsManager";
import AddQuestion from "./pages/TestSeries/AddQuestionNew";
import EditQuestion from "./pages/TestSeries/EditQuestion";
import ViewQuestions from "./pages/TestSeries/ViewQuestions";
import Courses from "./pages/Courses/Courses";
import Leaderboard from "./pages/Results/Leaderboard";
import Subscriptions from "./pages/Subscriptions/Subscriptions";
import Users from "./pages/Users/Users";
import Settings from "./pages/Settings/Settings";

// Legacy "add-section" URLs now point to the sections manager
function LegacySectionsRedirect() {
  const { id } = useParams();
  return <Navigate to={`/admin/test-series/${id}/sections`} replace />;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check for existing authentication on app load
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const storedUserData = localStorage.getItem("userData");

    if (token && storedUserData) {
      try {
        JSON.parse(storedUserData);
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem("authToken");
        localStorage.removeItem("userData");
      }
    }

    setLoading(false);
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
  };

  if (loading) {
    return (
      <div className="app-boot">
        <img src="/logo.png" alt="" className="app-boot-logo" />
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/admin/dashboard" replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />

          <Route path="/reset-password" element={<ResetPassword />} />

          <Route
            path="/admin"
            element={
              isAuthenticated ? (
                <AdminLayout onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          >
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="test-series" element={<TestSeriesList />} />
            <Route path="test-series/create" element={<CreateTestSeries />} />
            <Route path="test-series/:id/edit" element={<EditTestSeries />} />
            <Route path="test-series/:id/sections" element={<SectionsManager />} />
            <Route path="test-series/:id/add-section" element={<LegacySectionsRedirect />} />
            <Route
              path="test-series/:seriesId/section/:sectionId/add-question"
              element={<AddQuestion />}
            />
            <Route
              path="test-series/:seriesId/section/:sectionId/edit-question/:questionId"
              element={<EditQuestion />}
            />
            <Route
              path="test-series/:seriesId/section/:sectionId/questions"
              element={<ViewQuestions />}
            />
            <Route path="courses" element={<Courses />} />
            <Route path="users" element={<Users />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="results" element={<Navigate to="/admin/results/leaderboard" replace />} />
            <Route path="results/leaderboard" element={<Leaderboard />} />
            <Route path="results/*" element={<Navigate to="/admin/results/leaderboard" replace />} />
            <Route path="settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </Route>

          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
